/**
 * @erp/scanner-input/react — the ONLY module that imports React (an optional
 * peer dependency). The core `.` entry stays dependency-free.
 *
 * The hook is deliberately thin: subscription + state. All capture logic
 * lives in the pure machine and the DOM adapter.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CaptureError, CaptureSource, ScanResult } from "./types";
import { KeyboardWedgeSource, type KeyboardWedgeSourceOptions } from "./dom-adapter";

export interface UseScannerInputOptions extends KeyboardWedgeSourceOptions {
  /** Called once per scan (always the latest callback — safe to inline). */
  onScan?: (scan: ScanResult) => void;
  /**
   * Custom {@link CaptureSource} (Phase 4: BarcodeDetector / ZXing-wasm
   * camera sources plug in here). Default: a {@link KeyboardWedgeSource} on
   * `window` built from the wedge/parser options. Captured on first render;
   * later identity changes are ignored. The hook takes ownership of
   * `start`/`stop` for the source it uses.
   */
  source?: CaptureSource;
  /** Initial enabled state (capture starts immediately when true). Default true. */
  enabled?: boolean;
}

export interface UseScannerInputResult {
  /** Most recent scan, or `null` before the first one / after clearing. */
  lastScan: ScanResult | null;
  /**
   * Most recent capture failure reported by the source via `attachError`
   * (Phase-4 camera sources: permission denied, no camera, decoder failure),
   * or `null`. The keyboard wedge never reports errors. Sticky until the next
   * error, {@link clearLastError}, or re-enabling capture.
   */
  lastError: CaptureError | null;
  /** Whether the source is currently capturing. */
  enabled: boolean;
  /** Enable/disable capture (disabling stops the source and drops partial state). */
  setEnabled: (enabled: boolean) => void;
  /** Reset {@link lastScan} to `null`. */
  clearLastScan: () => void;
  /** Reset {@link lastError} to `null`. */
  clearLastError: () => void;
}

export function useScannerInput(options: UseScannerInputOptions = {}): UseScannerInputResult {
  const { onScan, source, enabled: initiallyEnabled, ...wedgeOptions } = options;

  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const [lastError, setLastError] = useState<CaptureError | null>(null);
  const [enabled, setEnabled] = useState(initiallyEnabled ?? true);

  const onScanRef = useRef(onScan);
  useEffect(() => {
    onScanRef.current = onScan;
  });

  // Resolve the source once, on first render (options are captured then).
  const sourceRef = useRef<CaptureSource | null>(null);
  if (sourceRef.current === null) {
    sourceRef.current = source ?? new KeyboardWedgeSource(wedgeOptions);
  }
  const activeSource = sourceRef.current;

  useEffect(() => {
    const detach = activeSource.attach((scan) => {
      setLastScan(scan);
      onScanRef.current?.(scan);
    });
    const detachError = activeSource.attachError?.((error) => {
      setLastError(error);
    });
    if (enabled) {
      setLastError(null); // (re-)enabling clears a stale failure
      activeSource.start();
    } else {
      activeSource.stop();
    }
    return () => {
      detach();
      detachError?.();
      activeSource.stop();
    };
  }, [activeSource, enabled]);

  const clearLastScan = useCallback(() => {
    setLastScan(null);
  }, []);

  const clearLastError = useCallback(() => {
    setLastError(null);
  }, []);

  return { lastScan, lastError, enabled, setEnabled, clearLastScan, clearLastError };
}

export type {
  CaptureError,
  CaptureErrorCode,
  CaptureSource,
  ScanResult,
  Symbology,
} from "./types";
